import { Component, DestroyRef, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { Select2Data } from 'ng-select2-component';
import { Subject, debounceTime, tap, switchMap, Subscription, interval, takeWhile } from 'rxjs';
import { NgIf, NgFor, NgClass, NgSwitch, NgSwitchCase, NgSwitchDefault, DatePipe } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { USER_PERMISSIONS } from '../../app.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AccountsReportRow,
  AccountsReportColumn,
  AccountsByManagerRow,
  AccountsByPropertyRow,
  AccountsRentBillingRow,
  AccountsReportSummary,
  ReportJobStatusResponse
} from '../../models/reports.model';

const BILL_TYPE_RENT = 1;
const BILL_TYPE_COMPANY_FEE = 3;

@Component({
  selector: 'app-accounts-reports',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    PaginationComponent,
    BreadcrumbComponent,
    NgSelectModule,
    NgIf,
    NgFor,
    NgClass,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    DatePipe,
  ],
  templateUrl: './accounts-reports.component.html',
  styleUrl: './accounts-reports.component.css'
})
export class AccountsReportsComponent implements OnInit, OnDestroy {
  breadcrumbList = {
    breadcrumb_path: 'Reports',
    currentURL: 'Accounts',
    breadcrumb_url: '/admin/reports/accounts'
  };

  // Tab control
  activeTab: 'all' | 'by-manager' | 'rent-billing' | 'by-property' | 'graphs' = 'all';

  // Pagination - All tab
  pageAll: number = 1;
  totalRowsAll: number = 20;
  totalPageAll: number = 0;
  pageCountAll: number = 1;

  // Pagination - By Manager tab
  pageByManager: number = 1;
  totalRowsByManager: number = 20;
  totalPageByManager: number = 0;
  pageCountByManager: number = 1;

  // Pagination - Monthly Rent Billing tab (manager × month rows)
  pageRentBilling: number = 1;
  totalRowsRentBilling: number = 24;
  totalPageRentBilling: number = 0;
  pageCountRentBilling: number = 1;

  // Pagination - By Property tab
  pageByProperty: number = 1;
  totalRowsByProperty: number = 20;
  totalPageByProperty: number = 0;
  pageCountByProperty: number = 1;

  // Data
  allBillsData: AccountsReportRow[] = [];
  byManagerData: AccountsByManagerRow[] = [];
  byPropertyData: AccountsByPropertyRow[] = [];
  rentBillingData: AccountsRentBillingRow[] = [];
  summaryData: AccountsReportSummary | null = null;

  // Filter form
  filterForm!: FormGroup;
  disableClear: boolean = false;

  // Available columns (from server) and currently-checked column keys
  availableColumns: AccountsReportColumn[] = [];
  selectedColumnKeys: Set<string> = new Set();
  showColumnPicker = false;

  // Sort state for the All Bills tab. Ascending by default, per spec.
  sortBy: string = 'creation_date';
  sortOrder: 'ASC' | 'DESC' = 'ASC';

  // Bank accounts dropdown (partner-wide, single-select)
  bankAccounts: any[] = [];

  // Property dropdown
  propertiesList: any[] = [];
  propertiesItems: Select2Data = [];
  searchProperty$ = new Subject<string>();
  private propertyPage = 1;
  private propertyPageSize = 20;
  private hasMoreProperties = true;

  // Manager dropdown
  managersList: any[] = [];
  managersItems: Select2Data = [];
  searchManager$ = new Subject<string>();
  private managerPage = 1;
  private managerPageSize = 20;
  private hasMoreManagers = true;

  // Status options
  statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Paid', label: 'Paid' },
    { value: 'Unpaid', label: 'Unpaid' },
    { value: 'Overdue', label: 'Overdue' },
    { value: 'Cancelled', label: 'Cancelled' }
  ];

  // Bill type options
  billTypeOptions = [
    { value: 1, label: 'Rent/Security Deposit' },
    { value: 2, label: 'Expenses' },
    { value: 3, label: 'Company Fee' },
    { value: 4, label: 'Company Expense' }
  ];

  // Invoice filter options
  invoiceOptions = [
    { value: 'true', label: 'Invoice Generated' },
    { value: 'false', label: 'Non-Invoice' }
  ];

  // Download state
  isDownloading: boolean = false;
  downloadProgress: string = '';
  activeDownloadType: 'pdf' | 'csv' | 'email' | 'whatsapp' | null = null;

  // Share modal state
  showShareModal: boolean = false;
  shareType: 'email' | 'whatsapp' = 'email';
  shareForm!: FormGroup;

  // Services
  private httpService = inject(HttpServiceService);
  private authService = inject(LocalStorageService);
  loadingService = inject(LoadingService);
  private destroyRef = inject(DestroyRef);

  // Subscriptions
  private allReportSubscription?: Subscription;
  private byManagerSubscription?: Subscription;
  private byPropertySubscription?: Subscription;
  private rentBillingSubscription?: Subscription;
  private propertySearchSubscription?: Subscription;
  private managerSearchSubscription?: Subscription;

  constructor(
    private fb: FormBuilder,
    private alertService: NgxToastrService
  ) {
    this.initFilterForm();
    this.initShareForm();
  }

  ngOnInit(): void {
    this.setupPropertySearch();
    this.setupManagerSearch();
    this.setupDateValidation();
    this.loadBankAccounts();
    this.loadAllReport();
    this.loadByManagerReport();
    this.loadByPropertyReport();
    this.loadRentBillingReport();
  }

  private loadBankAccounts(): void {
    this.httpService.getBankAccounts(1, 200).subscribe({
      next: (resp) => {
        this.bankAccounts = resp?.data?.bank_accounts ?? [];
      },
    });
  }

  private initFilterForm(): void {
    const defaults = this.defaultFilterValues();
    this.filterForm = this.fb.group({
      start_date: [defaults.start_date],
      end_date: [{ value: defaults.end_date, disabled: !defaults.start_date }],
      managed_by: [[]],
      property_id: [null],
      status: [null],
      bill_type: [defaults.bill_type],
      invoice_generated: [null],
      bank_account_id: [null]
    });
  }

  /** Default filters: current financial year (1 Apr – 31 Mar) and Company Fee. */
  private defaultFilterValues(): {
    start_date: string;
    end_date: string;
    managed_by: string[];
    property_id: null;
    status: null;
    bill_type: number[];
    invoice_generated: null;
    bank_account_id: null;
  } {
    const today = new Date();
    const fyStartYear = today.getMonth() < 3 ? today.getFullYear() - 1 : today.getFullYear();
    return {
      start_date: `${fyStartYear}-04-01`,
      end_date: `${fyStartYear + 1}-03-31`,
      managed_by: [],
      property_id: null,
      status: null,
      bill_type: [BILL_TYPE_COMPANY_FEE],
      invoice_generated: null,
      bank_account_id: null
    };
  }

  private initShareForm(): void {
    this.shareForm = this.fb.group({
      selectedUser: [null],
      file_type: ['pdf'],
      first_name: [''],
      last_name: [''],
      email: [''],
      country_code: [''],
      whatsapp: ['']
    });
  }

  reportTypeOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'csv', label: 'CSV' }
  ];

  private setupDateValidation(): void {
    this.filterForm.get('start_date')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const endDateControl = this.filterForm.get('end_date');
        if (value) {
          endDateControl!.enable();
        } else {
          endDateControl!.disable();
          endDateControl!.reset();
        }
      });
  }

  private setupPropertySearch(): void {
    this.propertySearchSubscription = this.searchProperty$
      .pipe(
        debounceTime(300),
        tap(() => {
          this.propertyPage = 1;
          this.hasMoreProperties = true;
        }),
        switchMap((term) => {
          // status=all: list Archived properties too, so their bills can be filtered on.
          const query = `&status=all${term ? `&name=${term}` : ''}`;
          return this.httpService.getAllProperties(this.propertyPage, this.propertyPageSize, query);
        })
      )
      .subscribe((response) => {
        this.propertiesList = response.data?.properties ?? [];
        this.hasMoreProperties = this.propertiesList.length < (response.data?.total ?? 0);
        this.propertiesItems = this.propertiesList.map((p: any) => ({
          value: p.uid,
          label: p.name,
          data: { name: p.name }
        }));
      });
    this.searchProperty$.next('');
  }

  private setupManagerSearch(): void {
    this.managerSearchSubscription = this.searchManager$
      .pipe(
        debounceTime(300),
        tap(() => {
          this.managerPage = 1;
          this.hasMoreManagers = true;
        }),
        switchMap((term) => {
          const search = term ? term : '';
          return this.httpService.getPropertyManagers(this.managerPage, this.managerPageSize, search);
        })
      )
      .subscribe((response) => {
        this.managersList = response.data?.users ?? [];
        this.hasMoreManagers = this.managersList.length < (response.data?.total ?? 0);
        this.managersItems = this.managersList.map((m: any) => ({
          value: m.uid,
          label: `${m.first_name} ${m.last_name}`,
          data: {
            first_name: m.first_name,
            last_name: m.last_name,
            email: m.email,
            phone: m.phone,
            country_code: m.country_code
          }
        }));
      });
    this.searchManager$.next('');
  }

  onShareUserChange(selectedItem: any): void {
    if (selectedItem && selectedItem.data) {
      this.shareForm.patchValue({
        first_name: selectedItem.data.first_name || '',
        last_name: selectedItem.data.last_name || ''
      });

      if (this.shareType === 'email') {
        this.shareForm.patchValue({ email: selectedItem.data.email || '' });
      } else {
        this.shareForm.patchValue({
          country_code: selectedItem.data.country_code || '',
          whatsapp: selectedItem.data.phone || ''
        });
      }
    }
  }

  onPropertyScrollEnd(): void {
    if (!this.hasMoreProperties) return;
    this.propertyPage++;
    this.httpService.getAllProperties(this.propertyPage, this.propertyPageSize, '&status=all')
      .subscribe((resp) => {
        const more = resp.data?.properties ?? [];
        this.propertiesList = [...this.propertiesList, ...more];
        this.hasMoreProperties = this.propertiesList.length < (resp.data?.total ?? 0);
        this.propertiesItems = this.propertiesList.map((p: any) => ({
          value: p.uid,
          label: p.name,
          data: { name: p.name }
        }));
      });
  }

  onManagerScrollEnd(): void {
    if (!this.hasMoreManagers) return;
    this.managerPage++;
    this.httpService.getPropertyManagers(this.managerPage, this.managerPageSize)
      .subscribe((resp) => {
        const more = resp.data?.users ?? [];
        this.managersList = [...this.managersList, ...more];
        this.hasMoreManagers = this.managersList.length < (resp.data?.total ?? 0);
        this.managersItems = this.managersList.map((m: any) => ({
          value: m.uid,
          label: `${m.first_name} ${m.last_name}`,
          data: {
            first_name: m.first_name,
            last_name: m.last_name,
            email: m.email,
            phone: m.phone,
            country_code: m.country_code
          }
        }));
      });
  }

  switchTab(tab: 'all' | 'by-manager' | 'rent-billing' | 'by-property' | 'graphs'): void {
    this.activeTab = tab;
  }

  /** The Manager filter is multi-select; normalise whatever ng-select hands back to a uid list. */
  private managerIdsFromValue(value: any): string[] {
    const list = Array.isArray(value) ? value : value ? [value] : [];
    return list
      .map((v: any) => (v && typeof v === 'object' ? v.value : v))
      .filter((v: any) => !!v);
  }

  private buildFilterQuery(): string {
    let query = '';
    const formValue = this.filterForm.getRawValue();

    if (formValue.start_date) {
      query += `&start_date=${formValue.start_date}`;
    }
    if (formValue.end_date) {
      query += `&end_date=${formValue.end_date}`;
    }
    const managerIds = this.managerIdsFromValue(formValue.managed_by);
    if (managerIds.length) query += `&managed_by=${managerIds.join(',')}`;
    if (formValue.property_id) {
      const propertyId = typeof formValue.property_id === 'object' ? formValue.property_id.value : formValue.property_id;
      if (propertyId) query += `&property_id=${propertyId}`;
    }
    if (formValue.status) {
      query += `&status=${formValue.status}`;
    }
    if (formValue.bill_type && formValue.bill_type.length > 0) {
      query += `&bill_type=${formValue.bill_type.join(',')}`;
    }
    if (formValue.invoice_generated !== null && formValue.invoice_generated !== undefined) {
      query += `&invoice_generated=${formValue.invoice_generated}`;
    }
    if (formValue.bank_account_id) {
      query += `&bank_account_id=${formValue.bank_account_id}`;
    }

    if (this.selectedColumnKeys.size && this.availableColumns.length) {
      const cols = this.availableColumns
        .map((c) => c.key)
        .filter((k) => this.selectedColumnKeys.has(k));
      if (cols.length) query += `&columns=${encodeURIComponent(cols.join(','))}`;
    }

    query += `&sort_by=${this.sortBy}&sort_order=${this.sortOrder}`;

    return query;
  }

  /** The Monthly Rent Billing report only takes the date range and manager filters. */
  private buildRentBillingQuery(): string {
    let query = '';
    const formValue = this.filterForm.getRawValue();
    if (formValue.start_date) query += `&start_date=${formValue.start_date}`;
    if (formValue.end_date) query += `&end_date=${formValue.end_date}`;
    const managerIds = this.managerIdsFromValue(formValue.managed_by);
    if (managerIds.length) query += `&managed_by=${managerIds.join(',')}`;
    return query;
  }

  loadRentBillingReport(): void {
    this.rentBillingSubscription?.unsubscribe();
    this.rentBillingSubscription = this.httpService
      .getAccountsRentBilling(this.pageRentBilling, this.totalRowsRentBilling, this.buildRentBillingQuery())
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.rentBillingData = response.data.rows ?? [];
            this.totalPageRentBilling = response.data.total ?? 0;
            this.pageCountRentBilling = response.data.pageCount ?? 1;
          }
        },
        error: () => {
          this.alertService.error('Failed to load monthly rent billing report', 'toast-top-right');
        }
      });
  }

  pageChangeRentBilling(page: number): void {
    if (this.pageRentBilling === page) return;
    this.pageRentBilling = page;
    this.loadRentBillingReport();
  }

  get visibleColumns(): AccountsReportColumn[] {
    return this.availableColumns.filter((c) => this.selectedColumnKeys.has(c.key));
  }

  toggleColumn(key: string, checked: boolean): void {
    if (checked) this.selectedColumnKeys.add(key);
    else this.selectedColumnKeys.delete(key);
    this.selectedColumnKeys = new Set(this.selectedColumnKeys);
  }

  toggleColumnPicker(): void {
    this.showColumnPicker = !this.showColumnPicker;
  }

  // The dropdown wrapper calls $event.stopPropagation on its own clicks, so
  // anything that bubbles to the document came from outside it.
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.showColumnPicker) this.showColumnPicker = false;
  }

  private hydrateColumns(available: AccountsReportColumn[] | undefined): void {
    if (!available || this.availableColumns.length) return;
    this.availableColumns = available;
    this.selectedColumnKeys = new Set(available.filter((c) => c.default).map((c) => c.key));
  }

  /** Toggle direction when re-sorting the same column, else start ascending. */
  sortColumn(col: AccountsReportColumn): void {
    if (!col.sort) return;
    if (this.sortBy === col.key) {
      this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortBy = col.key;
      this.sortOrder = 'ASC';
    }
    this.pageAll = 1;
    this.loadAllReport();
  }

  sortIconClass(col: AccountsReportColumn): string {
    if (!col.sort) return '';
    if (this.sortBy !== col.key) return 'bi-arrow-down-up text-muted';
    return this.sortOrder === 'ASC' ? 'bi-sort-down-alt' : 'bi-sort-up-alt';
  }

  loadAllReport(): void {
    const query = this.buildFilterQuery();
    this.allReportSubscription = this.httpService
      .getAccountsReportAll(this.pageAll, this.totalRowsAll, query)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.allBillsData = response.data.rows ?? [];
            this.summaryData = response.data.summary ?? null;
            this.totalPageAll = response.data.total ?? 0;
            this.pageCountAll = response.data.pageCount ?? 1;
            this.hydrateColumns(response.data.available_columns);
          }
        },
        error: () => {
          this.alertService.error('Failed to load accounts report', 'toast-top-right');
        }
      });
  }

  loadByManagerReport(): void {
    const query = this.buildFilterQuery();
    this.byManagerSubscription = this.httpService
      .getAccountsReportByManager(this.pageByManager, this.totalRowsByManager, query)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.byManagerData = response.data.rows ?? [];
            if (!this.summaryData) {
              this.summaryData = response.data.summary ?? null;
            }
            this.totalPageByManager = response.data.total ?? 0;
            this.pageCountByManager = response.data.pageCount ?? 1;
          }
        },
        error: () => {
          this.alertService.error('Failed to load by-manager report', 'toast-top-right');
        }
      });
  }

  loadByPropertyReport(): void {
    const query = this.buildFilterQuery();
    this.byPropertySubscription = this.httpService
      .getAccountsReportByProperty(this.pageByProperty, this.totalRowsByProperty, query)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.byPropertyData = response.data.rows ?? [];
            if (!this.summaryData) {
              this.summaryData = response.data.summary ?? null;
            }
            this.totalPageByProperty = response.data.total ?? 0;
            this.pageCountByProperty = response.data.pageCount ?? 1;
          }
        },
        error: () => {
          this.alertService.error('Failed to load by-property report', 'toast-top-right');
        }
      });
  }

  onFilterChange(): void {
    this.disableClear = true;
    this.pageAll = 1;
    this.pageByManager = 1;
    this.pageByProperty = 1;
    this.pageRentBilling = 1;
    this.loadAllReport();
    this.loadByManagerReport();
    this.loadByPropertyReport();
    this.loadRentBillingReport();
  }

  resetFilters(): void {
    this.filterForm.reset(this.defaultFilterValues());
    this.disableClear = false;
    this.pageAll = 1;
    this.pageByManager = 1;
    this.pageByProperty = 1;
    this.pageRentBilling = 1;
    this.loadAllReport();
    this.loadByManagerReport();
    this.loadByPropertyReport();
    this.loadRentBillingReport();
  }

  pageChangeAll(page: number): void {
    if (this.pageAll === page) return;
    this.pageAll = page;
    this.loadAllReport();
  }

  pageChangeByManager(page: number): void {
    if (this.pageByManager === page) return;
    this.pageByManager = page;
    this.loadByManagerReport();
  }

  pageChangeByProperty(page: number): void {
    if (this.pageByProperty === page) return;
    this.pageByProperty = page;
    this.loadByPropertyReport();
  }

  // Download functionality with polling
  downloadReport(fileType: 'pdf' | 'csv'): void {
    if (this.isDownloading) return;

    this.isDownloading = true;
    this.activeDownloadType = fileType;
    this.downloadProgress = 'Initiating...';

    const query = this.buildFilterQuery();
    let downloadFn;

    if (this.activeTab === 'by-manager') {
      downloadFn = this.httpService.downloadAccountsReportByManager(fileType, query);
    } else if (this.activeTab === 'rent-billing') {
      downloadFn = this.httpService.downloadAccountsRentBilling(fileType, this.buildRentBillingQuery());
    } else if (this.activeTab === 'by-property') {
      downloadFn = this.httpService.downloadAccountsReportByProperty(fileType, query);
    } else {
      downloadFn = this.httpService.downloadAccountsReportAll(fileType, query);
    }

    downloadFn.subscribe({
      next: (response) => {
        if (response.success && response.data?.job_id) {
          this.pollJobStatus(response.data.job_id);
        } else {
          this.resetDownloadState();
          this.alertService.error('Failed to initiate download', 'toast-top-right');
        }
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to initiate download', 'toast-top-right');
      }
    });
  }

  private resetDownloadState(): void {
    this.isDownloading = false;
    this.downloadProgress = '';
    this.activeDownloadType = null;
  }

  private pollJobStatus(jobId: string): void {
    const maxPollingTime = 30000;
    const pollingInterval = 1000;
    const startTime = Date.now();
    let pollCount = 0;

    this.checkJobStatus(jobId);

    const poll$ = interval(pollingInterval).pipe(
      takeWhile(() => {
        const elapsed = Date.now() - startTime;
        return elapsed < maxPollingTime && this.isDownloading;
      })
    );

    poll$.subscribe({
      next: () => {
        pollCount++;
        this.downloadProgress = `Checking... (${pollCount})`;
        this.checkJobStatus(jobId);
      },
      complete: () => {
        if (this.isDownloading) {
          this.resetDownloadState();
          this.alertService.error('Report generation timed out. Please try again.', 'toast-top-right');
        }
      }
    });
  }

  private checkJobStatus(jobId: string): void {
    this.httpService.getReportJobStatus(jobId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const jobData: ReportJobStatusResponse = response.data;

          if (jobData.status === 'completed' && jobData.file_url) {
            this.resetDownloadState();
            this.alertService.success('Report generated successfully!', 'toast-top-right');
            window.open(jobData.file_url, '_blank');
          } else if (jobData.status === 'failed') {
            this.resetDownloadState();
            this.alertService.error(jobData.error_message || 'Report generation failed', 'toast-top-right');
          }
        }
      },
      error: () => {
        // Continue polling on error
      }
    });
  }

  // Share modal methods
  openShareModal(type: 'email' | 'whatsapp'): void {
    this.shareType = type;
    this.shareForm.reset({ file_type: 'pdf' });
    this.showShareModal = true;
  }

  closeShareModal(): void {
    this.showShareModal = false;
    this.shareForm.reset({ file_type: 'pdf' });
  }

  submitShare(): void {
    const formValue = this.shareForm.getRawValue();
    const shareValue = this.shareType === 'email'
      ? formValue.email
      : formValue.whatsapp;

    if (!shareValue || shareValue.trim() === '') {
      this.alertService.error(`Please enter ${this.shareType === 'email' ? 'an email address' : 'a WhatsApp number'}`, 'toast-top-right');
      return;
    }

    this.closeShareModal();
    this.isDownloading = true;
    this.activeDownloadType = this.shareType;
    this.downloadProgress = 'Sending...';

    const query = this.buildFilterQuery();

    const shareData: any = {
      share_type: this.shareType,
      first_name: formValue.first_name?.trim() || '',
      last_name: formValue.last_name?.trim() || ''
    };

    if (this.shareType === 'email') {
      shareData.email = shareValue.trim();
    } else {
      shareData.country_code = formValue.country_code?.trim() || '';
      shareData.whatsapp = shareValue.trim();
    }

    const fileType = formValue.file_type || 'pdf';
    let downloadFn;

    if (this.activeTab === 'by-manager') {
      downloadFn = this.httpService.downloadAccountsReportByManager(fileType, query, shareData);
    } else if (this.activeTab === 'rent-billing') {
      downloadFn = this.httpService.downloadAccountsRentBilling(fileType, this.buildRentBillingQuery(), shareData);
    } else if (this.activeTab === 'by-property') {
      downloadFn = this.httpService.downloadAccountsReportByProperty(fileType, query, shareData);
    } else {
      downloadFn = this.httpService.downloadAccountsReportAll(fileType, query, shareData);
    }

    downloadFn.subscribe({
      next: (response) => {
        if (response.success && response.data?.job_id) {
          this.pollJobStatusForShare(response.data.job_id);
        } else {
          this.resetDownloadState();
          this.alertService.error('Failed to initiate share', 'toast-top-right');
        }
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to initiate share', 'toast-top-right');
      }
    });
  }

  private pollJobStatusForShare(jobId: string): void {
    const maxPollingTime = 30000;
    const pollingInterval = 1000;
    const startTime = Date.now();
    let pollCount = 0;

    this.checkJobStatusForShare(jobId);

    const poll$ = interval(pollingInterval).pipe(
      takeWhile(() => {
        const elapsed = Date.now() - startTime;
        return elapsed < maxPollingTime && this.isDownloading;
      })
    );

    poll$.subscribe({
      next: () => {
        pollCount++;
        this.downloadProgress = `Processing... (${pollCount})`;
        this.checkJobStatusForShare(jobId);
      },
      complete: () => {
        if (this.isDownloading) {
          this.resetDownloadState();
          this.alertService.error('Share request timed out. Please try again.', 'toast-top-right');
        }
      }
    });
  }

  private checkJobStatusForShare(jobId: string): void {
    this.httpService.getReportJobStatus(jobId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const jobData: ReportJobStatusResponse = response.data;

          if (jobData.status === 'completed') {
            this.resetDownloadState();
            this.alertService.success(`Report shared via ${this.shareType} successfully!`, 'toast-top-right');
          } else if (jobData.status === 'failed') {
            this.resetDownloadState();
            this.alertService.error(jobData.error_message || 'Failed to share report', 'toast-top-right');
          }
        }
      },
      error: () => {
        // Continue polling on error
      }
    });
  }

  // Permission check
  get checkDownloadPermission(): boolean {
    return this.authService.checkPermission(USER_PERMISSIONS.REPORTS.REPORTS_DOWNLOAD);
  }

  // Status badge class
  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'Pending': 'badge-warning',
      'Paid': 'badge-success',
      'Unpaid': 'badge-danger',
      'Overdue': 'badge-danger',
      'Cancelled': 'badge-secondary'
    };
    return statusClasses[status] || 'badge-secondary';
  }

  // Bill type badge class
  getBillTypeClass(billType: string): string {
    const typeClasses: { [key: string]: string } = {
      'Rent/Security Deposit': 'badge-primary',
      'Expenses': 'badge-warning',
      'Company Fee': 'badge-info',
      'Company Expense': 'badge-secondary'
    };
    return typeClasses[billType] || 'badge-secondary';
  }

  /** True when the Bill Type filter is Rent/Security Deposit and nothing else. */
  get isRentOnlyFilter(): boolean {
    const types: number[] = this.filterForm?.getRawValue().bill_type ?? [];
    return types.length === 1 && Number(types[0]) === BILL_TYPE_RENT;
  }

  get totalPropertiesHeader(): string {
    return this.isRentOnlyFilter ? 'Properties with Pending Rent' : 'Total Properties';
  }

  get totalPropertiesTooltip(): string {
    return this.isRentOnlyFilter
      ? 'Properties with at least one pending/unpaid/overdue Rent/Security Deposit bill in the filtered result (the paid-date range excludes unpaid bills, so clear the dates to see pending rent)'
      : 'Rent Occupied properties managed (current portfolio, not date-filtered)';
  }

  // Bill type totals as ordered array
  get billTypeTotalsList(): { label: string; amount: number }[] {
    const map = this.summaryData?.amount_by_bill_type;
    if (!map) return [];
    const order = ['Rent/Security Deposit', 'Expenses', 'Company Fee', 'Company Expense'];
    return order
      .filter(k => map[k] !== undefined)
      .map(k => ({ label: k, amount: map[k] }));
  }

  getBillTypeTotalClass(label: string): string {
    const classes: { [key: string]: string } = {
      'Rent/Security Deposit': 'text-primary',
      'Expenses': 'text-warning',
      'Company Fee': 'text-info',
      'Company Expense': 'text-secondary'
    };
    return classes[label] || 'text-dark';
  }

  // Get summary keys for iteration
  getSummaryKeys(obj: { [key: string]: number } | undefined): string[] {
    return obj ? Object.keys(obj) : [];
  }

  // Format currency
  formatAmount(amount: number): string {
    return '₹ ' + (amount?.toLocaleString('en-IN') || '0');
  }

  // Math helper for template
  Math = Math;

  ngOnDestroy(): void {
    this.allReportSubscription?.unsubscribe();
    this.byManagerSubscription?.unsubscribe();
    this.byPropertySubscription?.unsubscribe();
    this.rentBillingSubscription?.unsubscribe();
    this.propertySearchSubscription?.unsubscribe();
    this.managerSearchSubscription?.unsubscribe();
  }
}
